import React, { useMemo } from 'react';
import Funnel from '@components/Widgets/Funnel';
import Widget from '@components/Widgets/Widget';
import { Filter, severitiesStyle, SEVERITY_LABELS } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { useAvContext } from '../../../context/AvContextProvider';
import useQuerySql from '../../../hooks/useQuerySql';
import stringToFilter from '../../../utils/stringToFIlter';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { filtersFieldToIgnore } from '../ticket.types';
import { useBuildWhereClause } from '../useBuildWhereClause';
import SeverityCompareWidget from './SeverityCompareWidget';
import { EVariant, IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper } from './utils';

const severities = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const severityCompareConfig = {
  title: 'Findings by severity',
  sql: where => `SELECT
    ${severities
      .map(
        severity => `COUNT_DISTINCT(IF(finding.severity = '${severity}', finding._key, null)) as now_${severity},
                   COUNT_DISTINCT(IF(finding.original_severity = '${severity}', finding._key, null)) as prev_${severity}`
      )
      .join(',')}
      FROM uber_findings
      ${where}`,
  transformData: data => {
    const { id, ...currentData } = data[0];
    const getBarHeight = (value, total) => (value === 0 || total === 0 ? 0 : (value / total) * 100);
    const transData = Object.keys(currentData).reduce(
      (acc, curr) => {
        const severity = curr.split('_')[1];
        const prev = currentData[`prev_${severity}`];
        const now = currentData[`now_${severity}`];
        return {
          ...acc,
          totalFindings: acc[severity] ? acc.totalFindings : acc.totalFindings + Math.max(prev, now),
          [severity]: {
            now,
            prev,
          },
        };
      },
      { totalFindings: 0 }
    );
    const { totalFindings, ...result } = transData;
    return severities.map(severity => ({
      severity,
      prev: {
        value: result[severity].prev,
        height: getBarHeight(result[severity].prev, transData.totalFindings),
      },
      now: {
        value: result[severity].now,
        height: getBarHeight(result[severity].now, transData.totalFindings),
      },
    }));
  },
};

const findingDuplicationConfig = {
  title: 'Finding Duplication',
  sql: where =>
    `SELECT
         total_findings_granular AS finding_count,
         open_tickets_granular AS ticket_count,
         active_tickets_granular AS active_tickets
     FROM uber_findings
     ${where}`,
  transformData: data => {
    const [{ active_tickets: count3, finding_count: count1, ticket_count: count2 }] = data;
    return count1
      ? [
          { name: 'Findings', value: count1, color: 'var(--dataviz-color-sequential-blue-scale03)' },
          {
            name: 'Tickets',
            value: count2,
            color: 'var(--dataviz-color-sequential-blue-scale04)',
            ratio: Math.round(count1 / count2),
          },
          { name: 'Active Tickets', value: count3, color: 'var(--dataviz-color-sequential-blue-scale06)' },
        ].map(item => ({ ...item, displayValues: [item.value, item.ratio] }))
      : [];
  },
};

const summaryUtils = {
  activeFindingsBySeverity: {
    title: 'Active Findings by Severity',
    sql: where => `SELECT finding.severity as severity, active_findings_granular FROM uber_findings ${where} GROUP BY severity`,
    transformData: data => {
      const transData = data.reduce(
        (acc, curr) =>
          severities.includes(curr.severity)
            ? {
                ...acc,
                [curr.severity]: curr.active_findings,
              }
            : acc,
        {}
      );
      return [severities.reduce((acc, curr) => (transData[curr] ? { ...acc, [curr]: transData[curr] } : acc), {})];
    },
  },
  uniqueCves: {
    title: 'Unique CVEs',
    sql: where => `SELECT total_global_vulnerabilities_granular AS unique_cves FROM uber_findings ${where}`,
    transformData: data => [data.reduce((acc, curr) => ({ ...acc, unique_cves: curr.unique_cves }), {})],
  },
};

const getFindingsBySourceWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: {
      title: 'Findings Count by Source Name',
    },
    coords: {
      x: 0,
      y: 0,
      h: 3,
      w: 0,
    },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Pie,
  requests: [
    {
      filter: Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [
          {
            name: 'finding.source_names',
          },
        ],
        metrics: [
          {
            name: 'total_findings_granular',
            alias: 'total_findings_granular',
          },
        ],
      },
      groupBy: ['finding.source_names'],
      sorting: [
        {
          name: 'total_findings_granular',
          dir: SortDir.DESC,
        },
      ],
      top: {
        size: 5,
        groupOthers: false,
      },
      projectionId: {
        name: 'uber_findings',
        builtIn: true,
      },
      distinct: false,
    },
  ],
});

const FindingsViewCharts: React.FC<IEntityViewChartsProps> = ({
  filters: f,
  isExpanded = true,
  setIsExpanded,
  entityTypeId,
  extraFilters,
  searchFields,
}) => {
  const theme = useTheme();
  const {
    accountEntities: { fieldTypeMap },
    measurements,
  } = useAvContext();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));

  const measurementsTypeMap = measurements.visible.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), {});

  const filters = Object.keys(f).reduce(
    (acc, field) =>
      (!filtersFieldToIgnore.includes(field) || field === 'search') && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    { search: [] }
  );

  const filtersWhere = useBuildWhereClause({
    entityTypeId,
    filters,
    extra: extraFilters,
    allowMetrics: true,
    searchFields,
  });

  const { data: activeFindingsBySeverityData, isLoading: activeFindingsBySeverityLoading } = useQuerySql({
    key: 'activeFindingsBySeverity',
    sql: summaryUtils.activeFindingsBySeverity.sql(filtersWhere),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: summaryUtils.activeFindingsBySeverity.transformData,
  });

  const { data: uniqueCvesData, isLoading: uniqueCvesLoading } = useQuerySql({
    key: 'uniqueCves',
    sql: summaryUtils.uniqueCves.sql(filtersWhere),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: summaryUtils.uniqueCves.transformData,
  });

  const { data: findingDuplicationData, isLoading: findingDuplicationLoading } = useQuerySql({
    key: 'findingDuplication',
    sql: findingDuplicationConfig.sql(filtersWhere),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: data => findingDuplicationConfig.transformData(data),
  });

  const { search, ...filteredFilters } = filters;
  const entityExtraFiltersObject = stringToFilter(extraFilters || '');
  const regularFilters = filterToExpression({ ...fieldTypeMap, ...measurementsTypeMap }, filteredFilters);

  const queryObjectFilters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters, entityExtraFiltersObject] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: search?.[0],
    fieldTypeMap,
  });

  const findingsBySourceWidget = getFindingsBySourceWidget({ filters: queryObjectFilters, isSmallScreen });

  const summaryItems = useMemo(
    () => [
      {
        title: summaryUtils.activeFindingsBySeverity.title,
        data: activeFindingsBySeverityData,
        isLoading: activeFindingsBySeverityLoading,
        showPills: true,
        getPillsStyleFunc: key => ({ ...severitiesStyle.label, ...(severitiesStyle[key]?.label || {}) }),
        getLabelFunc: key => SEVERITY_LABELS[key],
        onSuccess: data => data?.[0],
      },
      {
        title: summaryUtils.uniqueCves.title,
        data: uniqueCvesData,
        isLoading: uniqueCvesLoading,
        onSuccess: data => data?.[0],
        showIcon: false,
        variant: EVariant.values,
      },
      {
        title: findingDuplicationConfig.title,
        data: findingDuplicationData,
        isLoading: findingDuplicationLoading,
        onSuccess: data =>
          data?.reduce((acc, curr) => (['Findings', 'Active Tickets'].includes(curr.name) ? { ...acc, [curr.name]: curr.value } : acc), {}),
        showIcon: false,
        variant: EVariant.transition,
      },
    ],
    [
      activeFindingsBySeverityData,
      activeFindingsBySeverityLoading,
      uniqueCvesData,
      uniqueCvesLoading,
      findingDuplicationData,
      findingDuplicationLoading,
    ]
  );

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          <Box sx={{ ...entityViewWidgetStyle, minWidth: '500px' }}>
            <Widget
              title={severityCompareConfig.title}
              titleVariant="h6"
              titleMargin={1}
              queryOptions={{ placeholderData: keepPreviousData, enabled: isExpanded }}
              transformData={severityCompareConfig.transformData}
              sql={severityCompareConfig.sql(filtersWhere)}
              sx={{ height: '100%', pb: 1 }}>
              <SeverityCompareWidget entityTypeId="Finding" />
            </Widget>
          </Box>
          <Box
            sx={{
              ...entityViewWidgetStyle,
              position: 'relative',
            }}>
            <DraggableWidget
              widget={findingsBySourceWidget}
              customToolbar={
                <Typography sx={{ whiteSpace: 'nowrap' }} variant="h6">
                  {findingsBySourceWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: findingsBySourceWidget })}
            </DraggableWidget>
          </Box>
          <Box sx={{ ...entityViewWidgetStyle }}>
            <Widget
              title={findingDuplicationConfig.title}
              titleVariant="h6"
              titleMargin={1}
              sql={findingDuplicationConfig.sql(filtersWhere)}
              transformData={data => findingDuplicationConfig.transformData(data)}
              sx={{ height: '100%', pb: 1 }}
              queryOptions={{ placeholderData: keepPreviousData, enabled: isExpanded }}>
              <Funnel height="100%" barSize={16} labelsSize={12} />
            </Widget>
          </Box>
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default FindingsViewCharts;
